import TouchAppIcon from '@mui/icons-material/TouchApp';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, CircularProgress, Dialog, LinearProgress, TextField, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    CustomerData,
    InstallationCommandResult,
    InstallationCommandResultStatusEnum,
    NewDevice,
} from '../../../../../backendsdk';
import { SubImage } from '../../../../../components/SubImage';
import type { ImageMetadata } from '../../../../../components/getSubImageDataUrl';
import { TrackedButton as Button } from '../../../../../components/TrackedComponents';
import useApi from '../../../../../hooks/api';
import useProfile from '../../../../../hooks/profile';
import palette from '../../../../ColorPalette';
import { WorkerParams } from '../SetupTool';
import ObjectBus from '../assets/feedback/passengers/object.svg';
import ObjectTruck from '../assets/feedback/property/object.svg';
import FixedContainer from '../ui/FixedContainer';
import { horizonLineTargetPixel, objectOnFloorTargetPixel } from '../utils/sideCam';
import { getStoredValue, setStoredValue } from '../utils/storage';
import { FrontFacingOverlay } from './FrontFacingOverlay';
import { InCabinOverlay } from './InCabinOverlay';
import { convertParam } from './InstallationParams';
import { SideCameraOverlay } from './SideCameraOverlay';

const MAX_DISTANCE_METERS = 30;
const MAX_DISTANCE_FEET = MAX_DISTANCE_METERS * 3.28084;

export const INSTRUCTIONS: Record<string, string[]> = {
    in_cabin: ['position', 'occlusion', 'backlight', 'lock'],
    front_facing: ['position'],
    bsd_left: ['camera_arrow', 'side_of_vehicle', 'side_camera_horizon'],
    bsd_right: ['camera_arrow', 'side_of_vehicle', 'side_camera_horizon'],
};

interface DeviceFeedbackProps {
    horizontalPixel?: number;
    groundPixel?: number;
    minDistance?: number;
    newDevice: NewDevice;
    customer: CustomerData;
    suffix: string;
    setWorkerParams: CallableFunction;
    onInstall: CallableFunction;
    onBack: CallableFunction;
    onNext: CallableFunction;
    dashboardLink: React.ReactNode;
    vehicleLength: string;
    bsdLeftHeight: string;
    bsdRightHeight: string;
}

const DeviceFeedback: React.FC<DeviceFeedbackProps> = (props) => {
    const [stage, setStage] = useState<number>(0);
    const [ffNoHorizonForm, setFFNoHorizonForm] = useState<boolean>(false);
    const [commandResult, setCommandResult] = useState<InstallationCommandResult>();
    const [isError, setIsError] = useState<boolean>(false);
    const [imageCounter, setImageCounter] = useState<number>(0);
    const [keyCounter, setKeyCounter] = useState<number>(0);
    const [isLoadingInstall, setIsLoadingInstall] = useState<boolean>(false);
    const [isErrorInstall, setIsErrorInstall] = useState<boolean>(false);
    const [objectOverlay, setObjectOverlay] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [enteredDistance, setEnteredDistance] = useState<string>(
        getStoredValue<string>('OE-entered-distance', props.suffix, ''),
    );
    const { t } = useTranslation();
    const { installApi } = useApi();
    const [streams, setStreams] = useState<Record<string, ImageMetadata>>({});
    const [currentStream, setCurrentStream] = useState<number>(0);
    const [imageMapping, setImageMapping] = useState<Record<string, ImageMetadata>>({});
    const [showStreamsWarning, setShowStreamsWarning] = useState<boolean>(false);
    const [isNoHorizonLeft, setIsNoHorizonLeft] = useState<boolean>(false);
    const [isNoHorizonRight, setIsNoHorizonRight] = useState<boolean>(false);
    const { profile } = useProfile();
    const relevantInstructions = Object.fromEntries(
        Object.entries(INSTRUCTIONS).map(([key, value]) => [key, [...value]]),
    );
    if (isNoHorizonLeft) {
        relevantInstructions.bsd_left[2] = 'no_horizon';
    }
    if (isNoHorizonRight) {
        relevantInstructions.bsd_right[2] = 'no_horizon';
    }

    const measurementSystem = profile.customer.settings.country === 'us' ? 'IMPERIAL' : 'METRIC';
    const hasStreams = !!streams && Object.keys(streams).length > 0 && !!commandResult?.FullImageUrl;
    const expectedCameras = props.customer.settings.installed_cameras;
    const availableCameras = expectedCameras.filter((camera) => !Object.keys(imageMapping).includes(camera));
    const imagePositions = Object.values(streams);

    const positionToStreamKey = Object.fromEntries(
        Object.entries(streams).map(([key, value]) => [JSON.stringify(value), key]),
    );
    const positionToCameraName = Object.fromEntries(
        Object.entries(imageMapping).map(([key, value]) => [JSON.stringify(value), key]),
    );

    // map original stream keys to camera names
    const streamMapping: Record<string, string> = {};
    Object.entries(imageMapping).forEach(([key, value]) => {
        if (JSON.stringify(value) in positionToStreamKey) {
            streamMapping[key] = positionToStreamKey[JSON.stringify(value)];
        }
    });

    const currentMappedCamera = positionToCameraName[JSON.stringify(imagePositions[currentStream])];

    const isUS = props.customer.settings.country === 'us';
    const isMetric = !isUS;
    const units = isMetric ? 'meters' : 'inches';
    const image = props.customer.settings.driving_type === 'passengers' ? ObjectBus : ObjectTruck;
    const minDistance = !!props.minDistance
        ? isMetric
            ? props.minDistance + 0.5
            : convertParam(props.minDistance + 0.5, 'METRIC', 'IMPERIAL')
        : undefined;
    const isDistanceValid =
        minDistance !== undefined &&
        !!enteredDistance &&
        !isNaN(parseFloat(enteredDistance)) &&
        parseFloat(enteredDistance) >= minDistance &&
        parseFloat(enteredDistance) <= (isMetric ? MAX_DISTANCE_METERS : MAX_DISTANCE_FEET);
    const stageCount = objectOverlay ? 4 : 2;

    useEffect(() => {
        setStoredValue<string>('OE-entered-distance', props.suffix, enteredDistance);
    }, [enteredDistance]);

    const updateImageMapping = (streamResuls: Record<string, ImageMetadata>) => {
        setImageMapping((prev) => {
            const override: Record<string, ImageMetadata> = {};
            if (!!streamResuls['InCabinCamera'] && !prev['in_cabin']) {
                override['in_cabin'] = streamResuls['InCabinCamera'];
            }
            if (!!streamResuls['FrontFacingCamera'] && !prev['front_facing']) {
                override['front_facing'] = streamResuls['FrontFacingCamera'];
            }
            if (Object.keys(override).length + 1 === expectedCameras.length) {
                const missingCamera = expectedCameras.find((camera) => !Object.keys(override).includes(camera));
                const missingCameraStream = Object.keys(streamResuls).find(
                    (key) => !['InCabinCamera', 'FrontFacingCamera'].includes(key),
                );
                if (!!missingCamera && !!missingCameraStream) {
                    override[missingCamera] = streamResuls[missingCameraStream];
                }
            }
            if (Object.keys(override).length > 0) {
                return { ...prev, ...override };
            }
            return prev;
        });
    };

    const getCommandStatus = () => {
        installApi
            .installConnectDeviceIdPost({ deviceId: props.newDevice.imei })
            .then((res) => {
                setImageCounter(0);
                setKeyCounter((prev) => prev + 1);
                setCommandResult(res.data);
                try {
                    const result = res.data.result ? JSON.parse(res.data.result) : undefined;
                    if (result) {
                        if (result['2'] !== undefined) {
                            // in Edmond v2.5.0 the second camera identifier is sent mistakenly as "2", while it should be "1"
                            // e.g. {"InCabinCamera":[0,0,640,480],"FrontFacingCamera":[864,0,864,480],"3":[0,480,864,480],"2":[864,480,864,480]}
                            // so we need to correct it
                            result['1'] = result['2'];
                            delete result['2'];
                        }
                        setStreams(result);
                        updateImageMapping(result);
                    }
                } catch (e) {
                    console.log('No streams provided');
                }
            })
            .catch(() => {
                setIsError(true);
            });
    };

    const handleInstall = (streamMapping?: Record<string, string>) => {
        setIsLoadingInstall(true);
        setIsErrorInstall(false);
        props
            .onInstall(streamMapping)
            .then(() => {
                props.onNext();
            })
            .catch(() => {
                setIsErrorInstall(true);
                setIsLoadingInstall(false);
            });
    };

    useEffect(getCommandStatus, []);

    useEffect(() => {
        if (
            commandResult?.status !== InstallationCommandResultStatusEnum.Failure &&
            commandResult?.status !== InstallationCommandResultStatusEnum.NoSupport
        ) {
            if (imageCounter >= 3) {
                // wait for progress bar animation to end
                const timerId = setTimeout(() => {
                    getCommandStatus();
                }, 400);
                return () => clearTimeout(timerId);
            } else {
                const timerId = setTimeout(() => {
                    setImageCounter((prev) => prev + 1);
                }, 1000);
                return () => clearTimeout(timerId);
            }
        }
    }, [imageCounter, commandResult?.status]);

    useEffect(() => {
        if (commandResult?.status === InstallationCommandResultStatusEnum.NoSupport) {
            handleInstall();
        }
    }, [commandResult]);

    useEffect(() => {
        const wrongCameraCount =
            Object.keys(streams).length > 0 && Object.keys(streams).length !== expectedCameras.length;
        setShowStreamsWarning(wrongCameraCount);
    }, [Object.keys(streams).length]);

    if (isLoadingInstall || isErrorInstall) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
                        {t(`setup_tool.installation.header`)}
                    </Typography>
                    <FixedContainer>
                        {isLoadingInstall ? (
                            <>
                                <CircularProgress />
                                <Typography textAlign="center" sx={{ fontWeight: 500, mt: 1 }}>
                                    {t(`setup_tool.installation.params`)}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <WarningIcon color="error" sx={{ fontSize: 24, mb: 0.5 }} />
                                <Typography variant="overline" color="error" sx={{ lineHeight: 1, mb: 2 }}>
                                    {t('setup_tool.installation.error')}
                                </Typography>
                                <Button id="btn-retry" variant="contained" onClick={() => handleInstall(streamMapping)}>
                                    {t('setup_tool.installation.retry')}
                                </Button>
                            </>
                        )}
                    </FixedContainer>
                </Box>
            </Box>
        );
    }

    let leftHorizontalPixel;
    if (expectedCameras.includes('bsd_left') && !!props.vehicleLength && !!props.bsdLeftHeight) {
        const vehicleLength = convertParam(parseFloat(props.vehicleLength), measurementSystem, 'METRIC');
        const bsdLeftHeight = convertParam(parseFloat(props.bsdLeftHeight), measurementSystem, 'METRIC');
        if (isNoHorizonLeft) {
            leftHorizontalPixel = objectOnFloorTargetPixel(vehicleLength, bsdLeftHeight);
        } else {
            leftHorizontalPixel = horizonLineTargetPixel(vehicleLength, bsdLeftHeight);
        }
    }
    let rightHorizontalPixel;
    if (expectedCameras.includes('bsd_right') && !!props.vehicleLength && !!props.bsdRightHeight) {
        const vehicleLength = convertParam(parseFloat(props.vehicleLength), measurementSystem, 'METRIC');
        const bsdRightHeight = convertParam(parseFloat(props.bsdRightHeight), measurementSystem, 'METRIC');
        if (isNoHorizonRight) {
            rightHorizontalPixel = objectOnFloorTargetPixel(vehicleLength, bsdRightHeight);
        } else {
            rightHorizontalPixel = horizonLineTargetPixel(vehicleLength, bsdRightHeight);
        }
    }

    const overlays: Record<string, ReactNode> = {
        in_cabin: <InCabinOverlay hardware={props.newDevice.hardware} />,
        front_facing: (
            <FrontFacingOverlay horizontalPixel={objectOverlay ? props.groundPixel : props.horizontalPixel} />
        ),
        bsd_left: <SideCameraOverlay horizontalPixel={leftHorizontalPixel} side="left" />,
        bsd_right: <SideCameraOverlay horizontalPixel={rightHorizontalPixel} side="right" />,
    };

    let content;
    const isStreaming = commandResult?.status === InstallationCommandResultStatusEnum.Streaming;
    if (isError || commandResult?.status === InstallationCommandResultStatusEnum.Failure) {
        content = (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography>{t(`setup_tool.device_feedback.error`)}</Typography>
                <Typography>{t(`setup_tool.troubleshoot.contact`)}</Typography>
            </Box>
        );
    } else if (commandResult?.status === InstallationCommandResultStatusEnum.Started) {
        content = (
            <FixedContainer>
                <TouchAppIcon sx={{ color: palette.primary, fontSize: 48, mb: 1 }} />
                <Box sx={{ width: '175px' }}>
                    <Typography id="press-driver-button" textAlign="center" sx={{ fontWeight: 500 }}>
                        {t(`setup_tool.device_feedback.press_driver_button`)}
                    </Typography>
                </Box>
            </FixedContainer>
        );
    } else if (isStreaming) {
        const noHorizonButton = !!props.minDistance ? (
            <Button
                id="btn-no-horizon"
                variant="contained"
                color="primary"
                onClick={() => {
                    setObjectOverlay((prev) => {
                        setFFNoHorizonForm(!prev);
                        if (prev) {
                            setEnteredDistance('');
                        }
                        return !prev;
                    });
                }}
            >
                {t(`setup_tool.device_feedback.${objectOverlay ? 'reset_no_horizon' : 'no_horizon'}`)}
            </Button>
        ) : null;
        const noHorizonForm = (
            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
                <img src={image} alt="object" style={{ width: '100%' }} />
                <Typography sx={{ my: 2 }}>
                    {t('setup_tool.device_feedback.object_instructions_1', {
                        minDistance,
                        units: t(`setup_tool.installation_params.units.${units}`),
                    })}
                </Typography>
                <TextField
                    fullWidth
                    required
                    label={t('setup_tool.device_feedback.object_distance', {
                        units: t(`setup_tool.installation_params.units.${units}`),
                    })}
                    value={enteredDistance}
                    onChange={(e) => setEnteredDistance(e.target.value)}
                    error={!!errorMessage}
                    helperText={errorMessage}
                />
            </Box>
        );

        const refreshProgress = (
            <LinearProgress
                key={`${keyCounter}-progress`}
                variant="determinate"
                value={(1 - imageCounter / 3) * 100}
                sx={{ width: '100%', flexShrink: 0 }}
            />
        );

        if (hasStreams) {
            if (ffNoHorizonForm) {
                content = noHorizonForm;
            } else {
                content = (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {!!currentMappedCamera ? (
                            <Typography>{t(`setup_tool.device_feedback.cameras.${currentMappedCamera}`)}</Typography>
                        ) : null}
                        {refreshProgress}
                        <div
                            style={{
                                width: '100%',
                                aspectRatio: `${imagePositions[currentStream][2]} / ${imagePositions[currentStream][3]}`,
                                position: 'relative',
                            }}
                        >
                            <SubImage
                                imageType={currentMappedCamera}
                                spriteUrl={
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    commandResult.FullImageUrl!
                                }
                                subImageData={imagePositions[currentStream]}
                                onError={() => setIsErrorInstall(true)}
                            />
                            {!!currentMappedCamera ? overlays[currentMappedCamera] : null}
                        </div>
                        {!currentMappedCamera ? (
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography fontSize={14} textAlign="center" sx={{ my: 1 }}>
                                    {t('setup_tool.device_feedback.choose_camera')}
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {availableCameras.map((cam) => {
                                        return (
                                            <Button
                                                key={cam}
                                                id={`btn-${cam}`}
                                                onClick={() => {
                                                    if (cam === 'front_facing') {
                                                        setStage(1);
                                                        setFFNoHorizonForm(false);
                                                    }
                                                    setImageMapping((prev) => {
                                                        const updated = {
                                                            ...prev,
                                                            [cam]: imagePositions[currentStream],
                                                        };
                                                        // if only one camera is left, map it as well
                                                        if (Object.keys(updated).length === imagePositions.length - 1) {
                                                            const availableCameras = expectedCameras.find(
                                                                (camera) => !Object.keys(updated).includes(camera),
                                                            );
                                                            const availablePositions = Object.values(streams).find(
                                                                (position) =>
                                                                    !Object.values(updated).includes(position),
                                                            );
                                                            if (!!availableCameras && !!availablePositions) {
                                                                updated[availableCameras] = availablePositions;
                                                            }
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                                variant="contained"
                                                color="primary"
                                            >
                                                {t(`setup_tool.device_feedback.cameras.${cam}`)}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ) : null}
                        {!!currentMappedCamera && Object.keys(relevantInstructions).includes(currentMappedCamera) ? (
                            <>
                                <ul style={{ width: '100%', paddingLeft: '20px' }}>
                                    {ffNoHorizonForm ? (
                                        <li>
                                            <Typography>
                                                {t('setup_tool.device_feedback.object_instructions_2')}
                                            </Typography>
                                        </li>
                                    ) : (
                                        relevantInstructions[currentMappedCamera].map((item) => (
                                            <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                                <Typography>
                                                    {t(`setup_tool.device_feedback.${currentMappedCamera}_${item}`)}
                                                </Typography>
                                            </li>
                                        ))
                                    )}
                                </ul>
                                {(currentMappedCamera === 'bsd_left' && !isNoHorizonLeft) ||
                                (currentMappedCamera === 'bsd_right' && !isNoHorizonRight) ? (
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            id="btn-no-horizon"
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                currentMappedCamera === 'bsd_left'
                                                    ? setIsNoHorizonLeft(true)
                                                    : setIsNoHorizonRight(true);
                                            }}
                                            sx={{ mt: 1 }}
                                        >
                                            {t('setup_tool.device_feedback.no_horizon')}
                                        </Button>
                                    </Box>
                                ) : null}
                            </>
                        ) : null}
                        {currentMappedCamera === 'front_facing' && !ffNoHorizonForm ? noHorizonButton : null}
                    </Box>
                );
            }
        } else {
            // kept for backward compatibility
            if (stage === 0) {
                content = (
                    <Box
                        key={stage}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                        {refreshProgress}
                        <FixedContainer style={{ position: 'relative' }}>
                            <img
                                id="ic-image"
                                data-testid="ic-image"
                                src={commandResult.ICImageUrl}
                                width="100%"
                                height="100%"
                                style={{ display: 'block' }}
                            />
                            {overlays.in_cabin}
                        </FixedContainer>
                        <ul style={{ width: '100%', paddingLeft: '20px' }}>
                            {relevantInstructions.in_cabin.map((item) => (
                                <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                    <Typography>{t(`setup_tool.device_feedback.in_cabin_${item}`)}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Box>
                );
            } else if (stage === 1 || stage === 3) {
                content = (
                    <Box
                        key={stage}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                        {refreshProgress}
                        <FixedContainer style={{ position: 'relative' }}>
                            <img
                                id="ff-image"
                                data-testid="ff-image"
                                src={commandResult.FFImageUrl}
                                width="100%"
                                height="100%"
                                style={{ display: 'block' }}
                            />
                            {overlays.front_facing}
                        </FixedContainer>
                        <ul style={{ width: '100%', paddingLeft: '20px' }}>
                            {stage === 1 ? (
                                relevantInstructions.front_facing.map((item) => (
                                    <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                        <Typography>{t(`setup_tool.device_feedback.front_facing_${item}`)}</Typography>
                                    </li>
                                ))
                            ) : (
                                <li>
                                    <Typography>{t('setup_tool.device_feedback.object_instructions_2')}</Typography>
                                </li>
                            )}
                        </ul>
                        {!ffNoHorizonForm ? noHorizonButton : null}
                    </Box>
                );
            }
        }
    } else {
        content = (
            <FixedContainer>
                <CircularProgress id="pending" data-testid="pending" />
            </FixedContainer>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {showStreamsWarning ? (
                <Dialog
                    open={true}
                    onClose={() => {
                        if (Object.keys(streams).length < expectedCameras.length) {
                            setShowStreamsWarning(false);
                        }
                    }}
                    fullWidth
                    maxWidth="xs"
                >
                    <Box sx={{ display: 'flex', p: 1, flexDirection: 'column', alignItems: 'center' }}>
                        <Typography>
                            {t('setup_tool.device_feedback.stream_error', {
                                cameras: expectedCameras.length,
                                streams: Object.keys(streams).length,
                            })}
                        </Typography>
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button id="btn-restart" variant="contained" color="primary">
                                {t('setup_tool.device_feedback.restart')}
                            </Button>
                            {!!Object.keys(streams).length && Object.keys(streams).length < expectedCameras.length ? (
                                <Button
                                    id="btn-continue"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setShowStreamsWarning(false)}
                                    sx={{ ml: 1 }}
                                >
                                    {t('setup_tool.device_feedback.continue')}
                                </Button>
                            ) : null}
                        </Box>
                    </Box>
                </Dialog>
            ) : null}
            <Box
                sx={{
                    width: '100%',
                    height: isStreaming ? undefined : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Typography
                    id="device-feedback-header"
                    variant="h6"
                    textAlign="center"
                    sx={{ mb: !!currentMappedCamera ? 0 : 2 }}
                >
                    {t(`setup_tool.device_feedback.header`)}
                </Typography>
                {content}
            </Box>
            <Box sx={{ width: '100%', display: 'flex', mt: 'auto' }}>
                <Box sx={{ flex: 1, mr: 1 }}>
                    <Button
                        id="btn-back"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (ffNoHorizonForm) {
                                setFFNoHorizonForm(false);
                                return;
                            }
                            if (isStreaming && hasStreams) {
                                if (currentMappedCamera === 'bsd_left') {
                                    setIsNoHorizonLeft(false);
                                }
                                if (currentMappedCamera === 'bsd_right') {
                                    setIsNoHorizonRight(false);
                                }
                                if (currentStream === 0 && !currentMappedCamera) {
                                    props.onBack();
                                } else if (!!currentMappedCamera && currentStream < imagePositions.length - 1) {
                                    setImageMapping((prev) => {
                                        const newMapping = { ...prev };
                                        if (currentMappedCamera) {
                                            delete newMapping[currentMappedCamera];
                                        }
                                        if (currentStream === imagePositions.length - 2) {
                                            // handle second-to-last mapping
                                            const lastMappedCamera =
                                                positionToCameraName[
                                                    JSON.stringify(imagePositions[imagePositions.length - 1])
                                                ];
                                            if (lastMappedCamera) {
                                                delete newMapping[lastMappedCamera];
                                            }
                                        }
                                        return newMapping;
                                    });
                                } else {
                                    setCurrentStream((prev) => prev - 1);
                                }
                            } else {
                                setStage((prev) => {
                                    if (prev > 0) {
                                        setErrorMessage('');
                                        if (ffNoHorizonForm) {
                                            setObjectOverlay(false);
                                        }
                                        return prev - 1;
                                    } else {
                                        props.onBack();
                                        return prev;
                                    }
                                });
                            }
                        }}
                    >
                        {t('setup_tool.back')}
                    </Button>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Button
                        id="btn-next"
                        data-testid="btn-next"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            const showNoHorizonForm = hasStreams && ffNoHorizonForm;
                            if (isStreaming && hasStreams) {
                                if (!showNoHorizonForm && currentStream === imagePositions.length - 1) {
                                    handleInstall(streamMapping);
                                } else {
                                    if (ffNoHorizonForm) {
                                        if (isDistanceValid) {
                                            props.setWorkerParams((prev: WorkerParams) => ({
                                                ...prev,
                                                distance: isMetric
                                                    ? parseFloat(enteredDistance)
                                                    : convertParam(parseFloat(enteredDistance), 'METRIC', 'IMPERIAL'),
                                            }));
                                            setFFNoHorizonForm(false);
                                            setStage(3);
                                            setErrorMessage('');
                                        } else {
                                            setErrorMessage(
                                                t(`setup_tool.device_feedback.object_distance_error_${units}`, {
                                                    max: isMetric ? MAX_DISTANCE_METERS : Math.floor(MAX_DISTANCE_FEET),
                                                }),
                                            );
                                        }
                                    } else {
                                        setCurrentStream((prev) => prev + 1);
                                    }
                                }
                            } else {
                                setStage((prev) => {
                                    if (prev < stageCount - 1) {
                                        if (ffNoHorizonForm) {
                                            if (isDistanceValid) {
                                                props.setWorkerParams((prev: WorkerParams) => ({
                                                    ...prev,
                                                    distance: isMetric
                                                        ? parseFloat(enteredDistance)
                                                        : convertParam(
                                                              parseFloat(enteredDistance),
                                                              'METRIC',
                                                              'IMPERIAL',
                                                          ),
                                                }));
                                                return prev + 1;
                                            } else {
                                                setErrorMessage(
                                                    t(`setup_tool.device_feedback.object_distance_error_${units}`, {
                                                        max: isMetric
                                                            ? MAX_DISTANCE_METERS
                                                            : Math.floor(MAX_DISTANCE_FEET),
                                                    }),
                                                );
                                                return prev;
                                            }
                                        }
                                        return prev + 1;
                                    } else {
                                        handleInstall();
                                        return prev;
                                    }
                                });
                            }
                        }}
                        disabled={
                            (isStreaming && hasStreams && !currentMappedCamera) ||
                            commandResult?.status !== InstallationCommandResultStatusEnum.Streaming
                        }
                    >
                        {t('setup_tool.next')}
                    </Button>
                </Box>
            </Box>
            {props.dashboardLink}
        </Box>
    );
};

export default DeviceFeedback;
